import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { queryClient } from '@/api/queryClient'

/**
 * Active workspace context.
 *
 * `activeWsId === null` means "personal context" (resources where
 * workspace_id IS NULL on the backend). The axios request interceptor
 * reads `activeWsId` synchronously and stamps `X-Workspace-ID` on every
 * `/api/*` call.
 *
 * Persistence:
 *   - Key: `creo-workspace` (zustand `persist` JSON envelope).
 *   - `partialize` keeps ONLY `activeWsId` — `workspaces` is server data
 *     and gets re-fetched after auth bootstrap.
 *   - `onRehydrateStorage` performs a one-shot migration from the legacy
 *     plain-string key `active_workspace_id`. The legacy frontend stored
 *     a bare integer (e.g. `"5"`); we lift it into the new envelope on
 *     first run and delete the old key so the migration never repeats.
 *
 * `switchWorkspace(id)` invariant (architecture §4.3, PRD C-09):
 *   wipe ALL `['ws', ...]`-keyed queries BEFORE flipping `activeWsId`.
 *   Order matters — flipping first would let in-flight queries finalize
 *   under the new wsId and poison the cache with mixed data.
 */
export type Workspace = {
  id: number
  name: string
  emoji: string
  role: 'owner' | 'admin' | 'member'
}

export type WorkspaceState = {
  activeWsId: number | null
  workspaces: Workspace[]
  setWorkspaces: (ws: Workspace[]) => void
  switchWorkspace: (id: number | null) => void
  reset: () => void
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      activeWsId: null,
      workspaces: [],
      setWorkspaces: (ws) => set({ workspaces: ws }),
      switchWorkspace: (id) => {
        if (get().activeWsId === id) return
        // CRITICAL ORDER: wipe first, set second (architecture §4.3, C-09).
        queryClient.removeQueries({ queryKey: ['ws'] })
        set({ activeWsId: id })
      },
      reset: () => set({ activeWsId: null, workspaces: [] }),
    }),
    {
      name: 'creo-workspace',
      partialize: (s) => ({ activeWsId: s.activeWsId }),
      onRehydrateStorage: () => (state) => {
        if (typeof window === 'undefined') return
        const legacy = window.localStorage.getItem('active_workspace_id')
        if (legacy && state && state.activeWsId === null) {
          const parsed = parseInt(legacy, 10)
          if (!Number.isNaN(parsed)) {
            state.activeWsId = parsed
          }
          window.localStorage.removeItem('active_workspace_id')
        }
      },
    },
  ),
)
